import React, { useMemo, useState } from 'react';
import Modal from '../../ui/Modal';
import FileIcon from '../../icons/FileIcon';
import { MeetingFile } from './panels/MeetingMaterialsPanel';
import { isPdfFile } from '../../../types/deck';

interface MaterialPickerDialogProps {
  isOpen: boolean;
  files: MeetingFile[];
  busy?: boolean;
  onCancel: () => void;
  // Ordered list of selected PDF file ids to present.
  onStart: (materialFileIds: string[]) => void;
}

interface PickItem {
  file: MeetingFile;
  isPdf: boolean;
  selected: boolean;
}

// Secretary go-live material picker. Lists the meeting's materials; PDFs are
// selectable with checkboxes and can be reordered (arrows). Non-PDF files are
// greyed and cannot be presented in this phase. Defaults to all PDFs selected
// in listing order. "Start Live" reports the chosen ordered file ids.
const MaterialPickerDialog: React.FC<MaterialPickerDialogProps> = ({
  isOpen,
  files,
  busy,
  onCancel,
  onStart,
}) => {
  const initialItems = useMemo<PickItem[]>(
    () =>
      files.map((file) => {
        const isPdf = isPdfFile(file.filename);
        return { file, isPdf, selected: isPdf };
      }),
    [files]
  );

  const [items, setItems] = useState<PickItem[]>(initialItems);
  // Re-seed the selection when the materials list changes. The previous value
  // is tracked in state (not a ref): refs mutated during render survive a
  // discarded concurrent render while the paired setItems is lost, leaving the
  // picker permanently empty ("Start Live" disabled).
  const [prevFiles, setPrevFiles] = useState(files);
  if (prevFiles !== files) {
    setPrevFiles(files);
    setItems(initialItems);
  }

  const toggle = (fileId: string) =>
    setItems((prev) =>
      prev.map((it) =>
        it.file.file_id === fileId && it.isPdf ? { ...it, selected: !it.selected } : it
      )
    );

  const move = (index: number, delta: number) =>
    setItems((prev) => {
      const target = index + delta;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(index, 1);
      next.splice(target, 0, moved);
      return next;
    });

  const setAll = (selected: boolean) =>
    setItems((prev) => prev.map((it) => (it.isPdf ? { ...it, selected } : it)));

  const selectedIds = items.filter((it) => it.isPdf && it.selected).map((it) => it.file.file_id);
  const pdfCount = items.filter((it) => it.isPdf).length;

  return (
    <Modal isOpen={isOpen} onClose={onCancel} title="Select presentation materials" maxWidth="560px">
      <div className="material-picker">
        <p className="material-picker-hint">
          Choose the PDFs to present and set their order. Members will follow this deck.
        </p>

        {items.length === 0 ? (
          <div className="meeting-empty-hint">No materials uploaded for this meeting.</div>
        ) : (
          <>
            <div className="material-picker-toolbar">
              <button className="meeting-btn meeting-btn-secondary meeting-btn-sm" onClick={() => setAll(true)} disabled={pdfCount === 0}>
                Select all
              </button>
              <button className="meeting-btn meeting-btn-secondary meeting-btn-sm" onClick={() => setAll(false)} disabled={pdfCount === 0}>
                Select none
              </button>
            </div>

            <ul className="material-picker-list">
              {items.map((it, index) => (
                <li
                  key={it.file.file_id}
                  className={`material-picker-item ${it.isPdf ? '' : 'material-picker-item-disabled'}`}
                >
                  <input
                    type="checkbox"
                    checked={it.isPdf && it.selected}
                    disabled={!it.isPdf}
                    onChange={() => toggle(it.file.file_id)}
                    aria-label={`Select ${it.file.filename}`}
                  />
                  <FileIcon size={16} />
                  <span className="material-picker-name" title={it.file.filename}>
                    {it.file.filename}
                  </span>
                  {!it.isPdf && (
                    <span className="material-picker-badge">not presentable in this phase</span>
                  )}
                  <span className="material-picker-reorder">
                    <button
                      className="meeting-file-action-btn"
                      aria-label={`Move ${it.file.filename} up`}
                      disabled={index === 0}
                      onClick={() => move(index, -1)}
                    >
                      ↑
                    </button>
                    <button
                      className="meeting-file-action-btn"
                      aria-label={`Move ${it.file.filename} down`}
                      disabled={index === items.length - 1}
                      onClick={() => move(index, 1)}
                    >
                      ↓
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}

        <div className="material-picker-actions">
          <button className="meeting-btn meeting-btn-secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            className="meeting-btn meeting-btn-primary"
            onClick={() => onStart(selectedIds)}
            disabled={busy || selectedIds.length === 0}
          >
            {busy ? 'Starting…' : `Start Live${selectedIds.length ? ` (${selectedIds.length})` : ''}`}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default MaterialPickerDialog;
